<template>
  <div>
    <WdsMenu z-index="10" global>
      <template #trigger>
        <WdsButtonGhost class="tw-float-right" data-test="candidate-menu-trigger">
          <WdsIcon name="overflow-menu-horizontal" />
        </WdsButtonGhost>
      </template>
      <WdsMenuOption
        v-if="canShareJob"
        :disabled="inviteToApplyDisabled"
        data-test="candidate-menu-invite-to-apply"
        @click="showSendInviteModal = true"
      >
        {{ $t('Invite to apply') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="jobObj.viewerCanCreateHire && hireOfferLink"
        :href="hireOfferLink"
        data-test="candidate-menu-create-offer"
      >
        {{ $t('Create hire offer') }}
      </WdsMenuOption>
      <slot name="action" />
      <WdsMenuOption
        v-if="jobCandidate.bid?.message?.url"
        :href="jobCandidate.bid.message.url"
        data-test="candidate-menu-view-application"
      >
        {{ $t('View application') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="conversationLink"
        :href="conversationLink"
        data-test="candidate-menu-open-conversation"
      >
        {{ $t('Open conversation') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="canReject"
        theme="DESTRUCTIVE"
        data-test="candidate-menu-remove"
        @click="showRemoveCandidateModal = true"
      >
        {{ $t('Remove candidate') }}
      </WdsMenuOption>
    </WdsMenu>
    <teleport to="body">
      <WdsModal
        :show="showRemoveCandidateModal"
        custom-size="420px"
        data-test="candidate-menu-remove-candidate-modal"
        @cancel="showRemoveCandidateModal = false"
      >
        <template #header>
          <div class="gradient" />
        </template>

        <RemoveCandidateModal
          :company-id="jobObj.company.id"
          :job-id="jobObj.id"
          :job-candidate-id="jobCandidate.id"
          :job-candidate-worker-id="jobCandidate.worker.id"
          :job-candidate-worker-name="jobCandidate.worker.name"
          :job-candidate-worker-avatar="jobCandidate.worker.avatar"
          :job-candidate-worker-title="jobCandidate.worker.jobTitle ?? ''"
          :offer="jobCandidate?.bid ?? undefined"
          @cancel="showRemoveCandidateModal = false"
          @candidate-updated="handleCandidateUpdated"
        />
      </WdsModal>
      <WdsModal
        :show="showSendInviteModal"
        :title="$t('Send Invitation')"
        data-test="candidate-menu-send-invitiation-modal"
        @cancel="showSendInviteModal = false"
      >
        <SendCandidateInvitationModal
          :job-id="jobObj.id"
          :worker="jobCandidate.worker"
          @cancel="showSendInviteModal = false"
          @candidate-updated="handleCandidateUpdated"
        />
      </WdsModal>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { WdsButtonGhost, WdsIcon, WdsMenu, WdsMenuOption, WdsModal } from '@worksome/wds-components'

import { type FragmentType, useFragment } from '@/gql'
import { JobCandidate, JobCandidateHiringStep, JobCandidateStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

import { JobFragment } from '@/components/Job/graphql/fragments'
import RemoveCandidateModal from '@/components/Job/Candidates/RemoveCandidateModal.vue'
import SendCandidateInvitationModal from '@/components/Job/Candidates/SendCandidateInvitationModal.vue'

const props = defineProps<{
  job: FragmentType<typeof JobFragment>
  jobCandidate: JobCandidate
}>()

const jobObj = useFragment(JobFragment, props.job)

const emit = defineEmits(['show-drawer', 'candidate-updated'])

// --- Remove candidate logic ---
const showRemoveCandidateModal = ref(false)

const handleCandidateUpdated = () => {
  emit('candidate-updated')
  showRemoveCandidateModal.value = false
}

const hireOfferLink = computed(() => {
  if (
    props.jobCandidate.status === JobCandidateStatus.Eligible &&
    props.jobCandidate.step !== JobCandidateHiringStep.Hiring
  ) {
    if (props.jobCandidate.bid?.id) {
      return laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect_offer, {
        freelancer: props.jobCandidate.worker?.id,
        job: jobObj.id,
        offer: props.jobCandidate.bid.id,
      })
    }

    return laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
      freelancer: props.jobCandidate.worker?.id,
      job: jobObj.id,
    })
  }
  return null
})

const conversationLink = computed(() => {
  // Conversation on the hire takes priority
  if (props.jobCandidate.hire?.conversation) {
    return props.jobCandidate.hire.conversation.url
  }

  // Conversation from the offer
  if (props.jobCandidate.bid?.conversation) {
    return props.jobCandidate.bid.conversation.url
  }

  if (props.jobCandidate.job.conversations?.data?.length) {
    // Conversation between TC and company
    const existingConversation = props.jobCandidate.job.conversations?.data.find((conversation) =>
      conversation.participants?.data?.find(
        ({ account }) => account.id === props.jobCandidate.worker?.id,
      ),
    )

    if (existingConversation) {
      return existingConversation.url
    }
  }
  return null
})

// Send invitation logic
const showSendInviteModal = ref(false)

const canShareJob = computed(
  () =>
    jobObj.available &&
    jobObj.viewerCanEdit &&
    props.jobCandidate.status === JobCandidateStatus.Eligible &&
    (!props.jobCandidate.jobShare?.id || props.jobCandidate.jobShare?.isActive === false),
)

const inviteToApplyDisabled = computed(
  () => !jobObj.published || props.jobCandidate.step !== JobCandidateHiringStep.Shortlisted,
)

const canReject = computed(
  () =>
    props.jobCandidate.viewerCanUpdateStatus &&
    props.jobCandidate.status !== JobCandidateStatus.NonEligible &&
    props.jobCandidate.step !== JobCandidateHiringStep.Hiring,
)
</script>

<style scoped lang="scss">
.gradient {
  @apply tw-bg-gradient-to-r tw-from-indigo-400 tw-to-red-300;
  @apply tw-flex-grow tw--my-6 tw--ml-6 tw-rounded-t;
  margin-right: -3.75rem;
}
</style>
